import React from 'react'
import styled from 'styled-components'
import { Tooltip } from 'sutra/components/Tooltip/Tooltip'

import { EditGlobalFiltersLink } from 'components/EditGlobalFiltersLink'

export interface FilterCardAccentBarProps {
  /** 'local' = the tile's own filter (orange bar), 'global' = inherited from
   *  a parent scope (navy bar). */
  accent: 'local' | 'global'
  /** Which word the LOCAL tooltip's "this ___" uses. Only ever meaningful
   *  for `accent="local"`, since a local bar only ever renders inside a
   *  dashboard tile (a chart or a widget) — see the caller's doc comment.
   *  Falls back to the generic "tile" if omitted. */
  tileKind?: 'chart' | 'widget'
  /** Focuses the dashboard's Global filters panel. Only ever read for
   *  `accent="global"`. When absent, the GLOBAL tooltip omits its
   *  "Edit global filters" clause entirely rather than showing a dead,
   *  non-clickable one — this happens on purpose for the one caller where
   *  `accent="global"` means "this card IS a Global filters panel row",
   *  not "inherited from one": there's nothing to jump to from there. */
  onEditSource?: () => void
}

const ACCENT_LABEL: Record<'local' | 'global', string> = {
  local: 'Local filter',
  global: 'Global filter',
}

// Ownership accent colors — the brand navy/orange swatches from
// `sutra/sutra.css`'s CSS custom properties, not the `sutra/tokens/colors.ts`
// TS ramps (those are accent hues, not the brand pair this bar signals).
const ACCENT_COLOR: Record<'local' | 'global', string> = {
  local: 'var(--color-sunrise-coral-base)',
  global: 'var(--color-still-night)',
}

/**
 * The inset ownership bar on a filter card (navy = global, orange = local),
 * as a real element rather than a `::before` pseudo-element so it can host a
 * hover tooltip explaining what the color means. `HitArea` widens the hover
 * target beyond the 3px visual bar without changing what's drawn.
 */
export const FilterCardAccentBar: React.FC<FilterCardAccentBarProps> = ({
  accent,
  tileKind,
  onEditSource,
}) => {
  const title =
    accent === 'global' ? (
      <>
        Global filter. This filter affects your entire dashboard. It is applied
        to every tile.
        {onEditSource !== undefined && (
          <>
            {' '}
            <EditGlobalFiltersLink onClick={onEditSource} />
          </>
        )}
      </>
    ) : (
      `Local filter. This filter only affects this ${tileKind ?? 'tile'}. It is not applied to your entire dashboard.`
    )

  return (
    <Tooltip content={title} side="top" align="start">
      <HitArea aria-label={ACCENT_LABEL[accent]}>
        <Bar $accent={accent} />
      </HitArea>
    </Tooltip>
  )
}

const HitArea = styled.span`
  position: absolute;
  left: 4px;
  top: 8px;
  bottom: 8px;
  width: 11px;
  display: flex;
  align-items: stretch;
  justify-content: center;
`

const Bar = styled.span<{ $accent: 'local' | 'global' }>`
  width: 3px;
  border-radius: 2px;
  background-color: ${({ $accent }) => ACCENT_COLOR[$accent]};
`
